import DomCreate from './utils/domCreate.js';

export class TableFilter {
    options = {
        selector: 'table.table-filter',
    };

    constructor(userOptions = {}) {
        for (const prop in userOptions) {
            if (Object.prototype.hasOwnProperty.call(this.options, prop)) {
                this.options[prop] = userOptions[prop];
            }
        }

        this.initSelectors(document);
    }

    initSelectors(rootElement) {
        const tables = rootElement.querySelectorAll(this.options.selector)

        tables.forEach(tableElement => {
            this.initFilterDom(tableElement);
        });
    }

    initFilterDom(tableElement) {
        if (tableElement.dataset.tableFilter === undefined) { return false; }
        let filtersConfig;
        try {
            filtersConfig = JSON.parse(tableElement.dataset.tableFilter);
        } catch (e) {
            console.error('Failed to parse table filter data:', e);
            return false;
        }

        if (tableElement.tableFilters === undefined) {
            const filters = [];
            filtersConfig.forEach(filterConfig => {
                const filterClass = filterConfig.type === 'search' ? TableSearchFilter : TableColumnFilter;
                filters.push(new filterClass(tableElement, filterConfig));
            });
            tableElement.tableFilters = filters;
        }

        if (tableElement.tableFilterRow === undefined) {
            const filterRow = TableFilter._createDOMElement('div', ["columns", "is-mobile", "is-multiline", "is-vcentered", "table-filter-row"]);
            tableElement.tableFilters.forEach(filter => {
               filterRow.insertBefore(filter.createFilterDOM(), null);
            });
            tableElement.tableFilterRow = filterRow;
            tableElement.parentElement.insertBefore(filterRow, tableElement);
        }

        return true;
    }

    static _createDOMElement(tag, classes= [], attributes = {}) {
        return DomCreate.create(tag, classes, attributes);
    }

    static _normalizeText(text) {
        return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    static _filterTable(tableElement) {
        const filterFunctions = tableElement.tableFilters.map(filter => filter.getFilterFunction());
        const columnsElements = tableElement.querySelectorAll('thead th');
        const columnsFiltered = Array.from(columnsElements).filter(column => {
            return window.getComputedStyle(column).display !== 'none';
        });
        const columns = Array.from(columnsFiltered).map(column => column.dataset.name.toLowerCase());

        const rowElements = tableElement.querySelectorAll('tbody tr');
        rowElements.forEach(rowElement => {
            const row = {};
            const cellElements = Array.from(rowElement.querySelectorAll('td'));
            const cellFiltered = Array.from(cellElements).filter(cell => {
                return window.getComputedStyle(cell).display !== 'none';
            });
            columns.forEach((columnName, index) => {
                row[columnName] = cellFiltered[index].dataset.value;
            });

            const visible = filterFunctions.every(filterFunction => filterFunction(row));
            rowElement.style.display = visible ? '' : 'none';
        });
    }
}

class TableBaseFilter {
    tableElement = null;
    filterFunction = (row) => true;

    constructor(tableElement, config) {
        this.tableElement = tableElement;
    }

    createFilterDOM() {
        return document.createElement('div');
    }

    getFilterFunction() {
        return this.filterFunction;
    }
}

class TableSearchFilter extends TableBaseFilter {
    excludeColumns = [];
    label = '';

    constructor(tableElement, config) {
        super(tableElement, config);
        this.excludeColumns = config.exclude || [];
    }

    createFilterDOM() {
        const dom = TableFilter._createDOMElement('div', ["column", "is-narrow"]);
        dom.innerHTML = `
            <div class="field has-addons">
                <div class="control has-icons-left">
                    <input type="text" class="input is-small is-primary" placeholder="Hledej">
                    <span class="icon is-small is-left">
                      <i class="fa-solid fa-magnifying-glass"></i>
                    </span>
                </div>
                <div class="control">
                    <button class="button is-small is-primary">
                        <span class="icon">
                            <i class="fa-solid fa-xmark"></i>
                        </span>
                    </button>
                </div>
            </div>  
        `;

        const input = dom.querySelector('input');
        input.setAttribute('placeholder', this.label || 'Hledej')
        const button = dom.querySelector('button');
        const query_name = 'table-filter-search';

        const buildUrl = new URL(window.location.href);
        input.value = buildUrl.searchParams.get(query_name) || '';

        const changeEventListener = () => {
            const currentUrl = new URL(window.location.href);
            if (input.value === '') {
                this.filterFunction = (row) => true;
                currentUrl.searchParams.delete(query_name);
            } else {
                currentUrl.searchParams.set(query_name, input.value);
                this.filterFunction = (row) => {
                    let result = false;
                    Object.keys(row).forEach(columnName => {
                        if (this.excludeColumns.includes(columnName)) {
                            return;
                        }
                        result = result || TableFilter._normalizeText(row[columnName]).includes(TableFilter._normalizeText(input.value));
                    });
                    return result;
                };
            }
            window.history.replaceState({}, '', currentUrl.toString());
            TableFilter._filterTable(this.tableElement);
        };

        if (input.value !== '') {
            changeEventListener();
        }

        button.addEventListener('click', () => {
            input.value = '';
            changeEventListener();
        });

        input.addEventListener('input', changeEventListener);

        return dom;
    }
}

class TableColumnFilter extends TableBaseFilter {
    column = '';
    filters = [];

    constructor(tableElement, config) {
        super(tableElement, config);
        this.label = config.label;
        this.column = config.column;
        this.filters = config.filters || [];
    }

    createFilterDOM() {
        const dom = TableFilter._createDOMElement('div', ["column", "is-narrow"]);
        const field = TableFilter._createDOMElement('div', ["field"]);
        dom.insertBefore(field, null);
        const toggle = TableFilter._createDOMElement('div', ["toggle-select", "is-small"]);
        field.insertBefore(toggle, null);

        const query_name = 'table-filter-column-'+this.column;

        const buildUrl = new URL(window.location.href);
        let selected = buildUrl.searchParams.get(query_name) || 0;

        this.filters.forEach((filter, index) => {
            const label = TableFilter._createDOMElement('label', ["radio"]);
            const input = TableFilter._createDOMElement('input', [], {name: "table-filter-column-"+this.column, type: 'radio'});
            label.insertBefore(input, null);
            label.insertBefore(document.createTextNode(filter[0]), null);
            toggle.insertBefore(label, null);

            let changeListener = () => {
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set(query_name, index);
                window.history.replaceState({}, '', currentUrl.toString());
                this.filterFunction = (row) => {
                    return this.filterBy(filter, row[this.column]);
                };
                TableFilter._filterTable(this.tableElement);
            };

            if (selected == index) {
                input.setAttribute('checked', 'checked');
                changeListener();
            }

            input.addEventListener('change', changeListener);
        });
        return dom;
    }

    filterBy(filter, value) {
        if (filter.count < 1) { return true; }
        if (filter[1] === 'all') { return true; }
        if (filter[1] === '<') { return value < filter[2]; }
        if (filter[1] === '>') { return value > filter[2]; }
        if (filter[1] === '<=') { return value <= filter[2]; }
        if (filter[1] === '>=') { return value >= filter[2]; }
        if (filter[1] === '=') { return value == filter[2]; }
        return true;
    }
}